// §5.6 — session restore.
//
// Close the tab, come back tomorrow, and every window is where you left it. Three rules shape
// the implementation:
//
//  1. **Restore never blocks the desktop.** Every step is independently recoverable: a window
//     naming an app nobody installed is skipped (§18 F12), a blob an app cannot make sense of is
//     that app's problem and not the shell's, and a file that will not parse is simply no
//     session at all.
//  2. **Writes are debounced**, because a drag commits geometry on every `pointerup` and a
//     session write is a whole OPFS round trip.
//  3. **`pagehide` is the one that matters.** It is the last event a closing tab reliably gets,
//     and F16 makes the last active tab the owner of the layout.
import {
  SESSION_VERSION,
  parseSessionState,
  type Desktop,
  type SessionState,
  type SessionWindow,
  type Win,
} from '@hangar/shared';
import type { WmStore } from './store';

/** §5.6 — where a guest's session lives. The kernel's own area under `/System` (D-065). */
export const SESSION_PATH = '/System/.session.json';

/** The `/System`-writable view. Only the kernel holds one (D-065). */
export interface SessionStore {
  readText(path: string): Promise<string>;
  write(path: string, data: string): Promise<unknown>;
  exists(path: string): Promise<boolean>;
}

export interface SessionService {
  /** Mark the session changed. Writes after the debounce. */
  touch(): void;
  /** Write now — `pagehide` cannot wait for a timer. */
  flush(): Promise<void>;
  /** What is on disk, or `null` when there is nothing usable there. */
  read(): Promise<SessionState | null>;
  /** The state the store is in right now, in its persisted shape. */
  capture(): SessionState;
  /**
   * Recreate a saved session. Returns what happened, so the caller can say so: `restored` is the
   * windows that came back, `skipped` the ones whose app is no longer installed (§18 F12).
   */
  restore(state: SessionState): { restored: number; skipped: string[] };
  /** §4.1 `session.save` — an app's opaque blob, kept until the window closes. */
  saveBlob(instanceKey: string, blob: unknown): void;
  /** §4.1 `session.load` — what that instance saved last time, or `null`. */
  loadBlob(instanceKey: string): unknown;
  /** Subscribe to the store and the page lifecycle. Returns the detach. */
  attach(target?: SessionTarget): () => void;
}

/** The slice of `window`/`document` the lifecycle hooks need. Narrow, so tests can drive it. */
export interface SessionTarget {
  addEventListener(type: string, listener: () => void): void;
  removeEventListener(type: string, listener: () => void): void;
  /** `document.visibilityState`, when this target has one. */
  visibilityState?: string;
}

export interface SessionOptions {
  wm: WmStore;
  store: SessionStore;
  /** §9.5 — the picker grants a window holds, and how to give them back (D-092). */
  grants: {
    of: (instanceKey: string) => string[];
    restore: (instanceKey: string, paths: readonly string[]) => void;
  };
  /** Whether an app id can still be launched. A window naming a stranger is skipped (F12). */
  isInstalled: (appId: string) => boolean;
  /**
   * §18 F16 — "window layout is owned by the **last active** tab". A background tab keeps
   * working; it just does not get to write the layout out from under the one in front.
   */
  canWrite?: () => boolean;
  /** Called when this tab becomes the visible one, so it can claim ownership (F16). */
  onActive?: () => void;
  debounceMs?: number;
  /** Reported when a window is skipped, so the user is told rather than left guessing (F12). */
  onSkipped?: (appIds: string[]) => void;
  onError?: (error: unknown) => void;
}

/** One window in its persisted form — §5.6's field list exactly, and nothing else. */
export function toSessionWindow(win: Win): SessionWindow {
  return {
    id: win.id,
    appId: win.appId,
    instanceKey: win.instanceKey,
    rect: { ...win.rect },
    state: win.state,
    snap: win.snap ? { ...win.snap } : null,
    restoreRect: win.restoreRect ? { ...win.restoreRect } : null,
    desktopId: win.desktopId,
    z: win.z,
  };
}

/** Blobs for windows that no longer exist are dropped: a session must not grow forever. */
export function pruneBlobs(
  blobs: ReadonlyMap<string, unknown>,
  live: ReadonlySet<string>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, blob] of blobs) if (live.has(key)) out[key] = blob;
  return out;
}

export function createSessionService(options: SessionOptions): SessionService {
  const { wm, store } = options;
  const debounceMs = options.debounceMs ?? 2_000;
  const blobs = new Map<string, unknown>();

  let timer: ReturnType<typeof setTimeout> | null = null;
  let dirty = false;
  /** True while `restore` is running, so recreating windows does not schedule a write. */
  let restoring = false;

  function capture(): SessionState {
    const state = wm.getState();
    const windows = wm.list();
    const live = new Set(windows.map((win) => win.instanceKey));
    return {
      v: SESSION_VERSION,
      desktops: state.desktops.map((desktop: Desktop) => ({ ...desktop })),
      activeDesktop: state.activeDesktopId,
      windows: windows.map(toSessionWindow),
      appState: pruneBlobs(blobs, live),
      grants: Object.fromEntries(
        windows
          .map((win) => [win.instanceKey, options.grants.of(win.instanceKey)] as const)
          .filter(([, paths]) => paths.length > 0),
      ),
      focused: state.focusedId,
    };
  }

  async function write(): Promise<void> {
    // F16 — not the owner, so not our layout to write. The change stays pending in case this
    // tab becomes the active one before it closes.
    if (options.canWrite && !options.canWrite()) return;
    dirty = false;
    try {
      await store.write(SESSION_PATH, JSON.stringify(capture()));
    } catch (error) {
      // A session that could not be saved is a lost layout, never a broken desktop.
      options.onError?.(error);
    }
  }

  function clearTimer(): void {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
  }

  const service: SessionService = {
    capture,

    touch() {
      if (restoring) return;
      dirty = true;
      clearTimer();
      timer = setTimeout(() => {
        timer = null;
        void write();
      }, debounceMs);
    },

    async flush() {
      clearTimer();
      if (!dirty) return;
      await write();
    },

    async read() {
      try {
        if (!(await store.exists(SESSION_PATH))) return null;
        return parseSessionState(await store.readText(SESSION_PATH));
      } catch (error) {
        options.onError?.(error);
        return null;
      }
    },

    restore(state) {
      restoring = true;
      const skipped: string[] = [];
      let restored = 0;
      try {
        wm.setDesktops(state.desktops);
        if (state.desktops.some((desktop) => desktop.id === state.activeDesktop)) {
          wm.setActiveDesktop(state.activeDesktop);
        }

        // Ascending z, so the recreated stack is in the order it was saved in.
        for (const saved of [...state.windows].sort((a, b) => a.z - b.z)) {
          if (!options.isInstalled(saved.appId)) {
            skipped.push(saved.appId);
            continue;
          }
          try {
            wm.create({
              id: saved.id,
              instanceKey: saved.instanceKey,
              appId: saved.appId,
              rect: saved.rect,
              desktopId: saved.desktopId,
              state: saved.state,
              z: saved.z,
              focus: false,
            });
            if (saved.snap) wm.setSnap(saved.id, saved.snap, saved.rect);
            // …and then the *saved* pre-snap rect, which `setSnap` cannot know: on a fresh
            // window it would remember the snapped rect as the thing to unsnap to (§5.4).
            wm.setRestoreRect(saved.id, saved.restoreRect);
            // §9.5 — the grants this window held. They still die when the window does.
            const held = state.grants[saved.instanceKey];
            if (held && held.length > 0) options.grants.restore(saved.instanceKey, held);
            restored += 1;
          } catch (error) {
            // One window that will not come back must not take the rest of the desktop with it.
            options.onError?.(error);
          }
        }

        const blobbed = state.appState;
        for (const key of Object.keys(blobbed)) blobs.set(key, blobbed[key]);

        if (state.focused !== null && wm.get(state.focused)) wm.focus(state.focused);
      } finally {
        restoring = false;
      }

      if (skipped.length > 0) options.onSkipped?.(skipped);
      return { restored, skipped };
    },

    saveBlob(instanceKey, blob) {
      blobs.set(instanceKey, blob);
      service.touch();
    },

    loadBlob(instanceKey) {
      return blobs.has(instanceKey) ? blobs.get(instanceKey) : null;
    },

    attach(target) {
      const unsubscribe = wm.store.subscribe(() => service.touch());
      const listeners: Array<[string, () => void]> = [];

      if (target) {
        // `pagehide` is the last event a closing tab reliably gets; `visibilitychange` covers a
        // tab that is backgrounded and then discarded without one.
        const onHide = (): void => void service.flush();
        const onVisibility = (): void => {
          if (target.visibilityState === 'hidden') void service.flush();
          else options.onActive?.();
        };
        listeners.push(['pagehide', onHide], ['visibilitychange', onVisibility]);
        for (const [type, listener] of listeners) target.addEventListener(type, listener);
      }

      return () => {
        clearTimer();
        unsubscribe();
        if (target) for (const [type, listener] of listeners) target.removeEventListener(type, listener);
      };
    },
  };

  return service;
}
